(function () {
    'use strict';

    angular
        .module('app')
        .controller('NoteEditCtrl', NoteEditCtrl);

    NoteEditCtrl.$inject = [
        '$q',
        '$scope',
        '$routeParams',
        '$location',
        'WindowTitleService',
        'NotesService',
        'ItemService',
        'UnsavedChangesService',
        'NavigationAidService',
        'ObjectSaveErrorHandlerService',
        'TagsService',
        'DialogService',
        'LastUpdatedService'];

    /**
     * Controller for note add/edit page.
     */
    function NoteEditCtrl(
        $q,
        $scope,
        $routeParams,
        $location,
        WindowTitleService,
        NotesService,
        ItemService,
        UnsavedChangesService,
        NavigationAidService,
        ObjectSaveErrorHandlerService,
        TagsService,
        DialogService,
        LastUpdatedService) {

        /* jshint validthis:true */
        var vm = this;
        vm.note = null;
        vm.relatedItem = null;
        vm.loading = true;
        vm.notFound = false;
        vm.viewTitle = 'Add Note';
        vm.tags = null;
        vm.saveNote = saveNote;
        vm.deleteNote = deleteNote;
        vm.copyNote = copyNote;
        vm.close = close;
        Object.defineProperty(vm, 'lastUpdatedLabel', { get: function () { return LastUpdatedService.getLabel(vm.note); } });
        Object.defineProperty(vm, 'noteIsNew', {
            get: function () { return !($routeParams.id && $routeParams.id !== 'new'); }
        });
        Object.defineProperty(vm, 'noteId', {
            get: function () { return $routeParams.id; }
        });
        Object.defineProperty(vm, 'dirty', {
            get: function () { return UnsavedChangesService.dirty; }
        });

        activate();

        function activate() {
            updateViewTitle();

            $q.all([
                loadNote(),
                loadTags()
            ])
            .finally(function () {
                vm.loading = false;
            });
        }

        /**
         * Update Title to be shown at the top of the view and browser window/tab title.
         */
        function updateViewTitle() {
            var title = '';

            if (vm.noteIsNew) {
                title = 'Add Note';
            } else {
                title = 'Note';

                if (!_.isNil(vm.note) && !_.isNil(vm.note.Subject)) {
                    title += ' - ' + vm.note.Subject;
                }
            }

            // Truncate so the title isn't hugely long
            title = title.substr(0, 128);

            vm.viewTitle = title;
            WindowTitleService.setTitle(title);
        }

        /**
         * Load tags into the vm.tags variable.
         *
         * @returns {promise} A promise that resolves once the tags are loaded, or an error occurred.
         */
        function loadTags() {
            return TagsService.query({ tagUsage: 'Note' })
                .then(getTagsOk, getTagsError);

            function getTagsOk(data) {
                vm.tags = data.Tags;
            }

            function getTagsError(response) {
                DialogService.error('Error fetching Tags', response);
            }
        }

        /**
         * Load the note object into the vm.note variable.
         *
         * @returns {promise} A promise that resolves when the note is loaded, or an error occurred.
         */
        function loadNote() {
            var loadPromise;
            if (vm.noteIsNew) {
                if ($routeParams.copyNoteId) {
                    loadPromise = NotesService.get($routeParams.copyNoteId)
                        .then(function (newNote) {
                            // Reset properties that aren't to be copied.
                            delete newNote.Id;
                            delete newNote.UpdatedBy;
                            delete newNote.UpdatedByUserName;
                            delete newNote.UpdatedDate;
                            delete newNote.CreatedBy;
                            delete newNote.CreatedDate;
                            return newNote;
                        });
                } else {

                    // Have to convert route parameter to a number, because otherwise it's a string
                    var relatedItemId = Number($routeParams.relatedItemId);
                    if (!_.isNumber(relatedItemId)) {
                        relatedItemId = null;
                    }

                    var newNote = {
                        Id: null,
                        Subject: '',
                        NoteText: '',
                        RelatedItem: relatedItemId,
                        Tags: []
                    };
                    loadPromise = $q.when(newNote);
                }
            } else {
                loadPromise = NotesService.get(vm.noteId);
            }

            return loadPromise
                .then(getNoteOk, getNoteError);

            function getNoteOk(data) {
                vm.note = data;
                loadNoteRelatedItemDetail();

                UnsavedChangesService.register({
                    scope: $scope,
                    modelExpression: 'vm.note',
                    saveFunction: saveNote,
                    unsavedItemName: 'Note'
                });

                if (vm.noteIsNew && $routeParams.copyNoteId) {
                    UnsavedChangesService.dirty = true;
                }

                updateViewTitle();
            }

            function getNoteError(response) {
                if (response.status === 404) {
                    vm.notFound = true;
                } else {
                    DialogService.error('Error fetching Note', response);
                }
            }
        }

        /**
         * A note has a "related item" (Entity, Task, etc.)
         * Load extra details for this related item so they can be displayed.
         */
        function loadNoteRelatedItemDetail() {
            if (!vm.noteIsNew && !_.isNil(vm.note) && !_.isNil(vm.note.RelatedItem)) {
                ItemService.get(vm.note.RelatedItem)
                    .then(loadRelatedItemOk, loadRelatedItemError);
            }

            function loadRelatedItemOk(data) {
                vm.relatedItem = data;
            }

            function loadRelatedItemError(response) {
                DialogService.error('Error fetching Note related item', response);
            }
        }

        /**
         * Save the note object.
         *
         * @returns {promise} A promise that resolves once the note is saved, or there was an error saving the note.
         */
        function saveNote() {
            if (!_.isNil(vm.editNoteForm) && vm.editNoteForm.$invalid) {
                DialogService.messageList('Error saving Note', null, vm.editNoteForm.$error);
                return $q.reject();
            }

            return NotesService.save(vm.note)
                .then(saveNoteOk, saveNoteError);

            function saveNoteOk(savedNote) {
                if (vm.noteIsNew) {
                    UnsavedChangesService.dirty = false;
                    NavigationAidService.replacePage('notes/' + savedNote.Id);
                } else {
                    vm.note = savedNote;
                    vm.editNoteForm.$setPristine();

                    UnsavedChangesService.register({
                        scope: $scope,
                        modelExpression: 'vm.note',
                        saveFunction: saveNote,
                        unsavedItemName: 'Note'
                    });

                    updateViewTitle();
                }
            }

            function saveNoteError(response) {
                return ObjectSaveErrorHandlerService.handleObjectSaveError(
                    response,
                    vm.note,
                    'Note',
                    vm.saveNote,
                    function () {
                        return NotesService.get(vm.noteId);
                    });
            }
        }

        /**
         * Delete the current note.
         *
         * @returns {promise} A promise that resolves once the note is deleted.
         */
        function deleteNote() {
            return DialogService.confirm('Delete the Note?', 'Delete Note confirm')
                .then(deleteOk);

            function deleteOk() {
                NotesService.remove(vm.noteId)
                    .then(deleteNoteOk, deleteNoteError);

                function deleteNoteOk() {
                    NavigationAidService.goBack();
                }

                function deleteNoteError(response) {
                    DialogService.error('Error deleting Note', response);
                }
            }
        }

        /**
         * Navigate to an "Add Note" page with the current note loaded as a copy.
         */
        function copyNote() {
            if (UnsavedChangesService.dirty) {
                DialogService.unsavedChanges('Note')
                    .then(function () {
                        saveTask().then(function () {
                            $location.url('notes/new?copyNoteId=' + vm.note.Id);
                        });
                    });
            } else {
                $location.url('notes/new?copyNoteId=' + vm.note.Id);
            }
        }

        function close() {
            NavigationAidService.goBack();
        }
    }
})();